/**
 * Admin questionnaire + position-types — typed api-client calls + TanStack Query
 * hooks (feature E05-F04, T03 + T04).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport uses
 * the shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) and `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact same
 * shapes as the Fastify API.
 *
 * Endpoints (1:1 with the legacy n8n manage_kickoff_questions / manage_position_types):
 *   GET    /admin/kickoff-questions          → useKickoffQuestions()
 *   POST   /admin/kickoff-questions          → useCreateKickoffQuestion()
 *   PATCH  /admin/kickoff-questions/:id        → useUpdateKickoffQuestion()
 *   DELETE /admin/kickoff-questions/:id        → useDeleteKickoffQuestion()  (soft)
 *   GET    /admin/position-types             → usePositionTypes()
 *   POST   /admin/position-types             → useCreatePositionType()
 *   PATCH  /admin/position-types/:id           → useUpdatePositionType()
 *   DELETE /admin/position-types/:id           → useDeletePositionType()     (soft)
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  adminMutationOkResponseSchema,
  kickoffQuestionCreateResponseSchema,
  kickoffQuestionsListResponseSchema,
  positionTypeCreateResponseSchema,
  positionTypesListResponseSchema,
  type KickoffQuestionCreateBody,
  type KickoffQuestionDTO,
  type KickoffQuestionUpdateBody,
  type PositionTypeDTO,
  type PositionTypeSaveBody,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query keys (kept out of the shared registry). */
export const KICKOFF_QUESTIONS_QUERY_KEY = ['admin', 'kickoff-questions'] as const;
export const POSITION_TYPES_QUERY_KEY = ['admin', 'position-types'] as const;

const KQ_COMMAND = 'manage_kickoff_questions';
const PT_COMMAND = 'manage_position_types';

// ===========================================================================
// KQ — kickoff questions
// ===========================================================================

/** Fetch all active kickoff questions (server orders sort_order, id). */
async function fetchKickoffQuestions(): Promise<KickoffQuestionDTO[]> {
  const result = await apiClient.get<unknown>('/admin/kickoff-questions', { command: KQ_COMMAND });
  return kickoffQuestionsListResponseSchema.parse(unwrap(result)).questions;
}

/** Create one kickoff question (category + text required). */
async function createKickoffQuestion(body: KickoffQuestionCreateBody): Promise<void> {
  const result = await apiClient.post<unknown>('/admin/kickoff-questions', body, {
    command: KQ_COMMAND,
  });
  kickoffQuestionCreateResponseSchema.parse(unwrap(result));
}

/** Update one kickoff question by id. */
async function updateKickoffQuestion(args: {
  id: number;
  body: KickoffQuestionUpdateBody;
}): Promise<void> {
  const result = await apiClient.patch<unknown>(`/admin/kickoff-questions/${args.id}`, args.body, {
    command: KQ_COMMAND,
  });
  adminMutationOkResponseSchema.parse(unwrap(result));
}

/** SOFT delete one kickoff question by id. */
async function deleteKickoffQuestion(id: number): Promise<void> {
  const result = await apiClient.delete<unknown>(`/admin/kickoff-questions/${id}`, {
    command: KQ_COMMAND,
  });
  adminMutationOkResponseSchema.parse(unwrap(result));
}

/** List query — the Questionnaire tab. */
export function useKickoffQuestions(): UseQueryResult<KickoffQuestionDTO[], ApiResultError> {
  return useQuery<KickoffQuestionDTO[], ApiResultError>({
    queryKey: KICKOFF_QUESTIONS_QUERY_KEY,
    queryFn: fetchKickoffQuestions,
  });
}

/** Create mutation — invalidates the list. */
export function useCreateKickoffQuestion(): UseMutationResult<
  void,
  ApiResultError,
  KickoffQuestionCreateBody
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, KickoffQuestionCreateBody>({
    mutationFn: createKickoffQuestion,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KICKOFF_QUESTIONS_QUERY_KEY });
    },
  });
}

/** Update mutation — invalidates the list. */
export function useUpdateKickoffQuestion(): UseMutationResult<
  void,
  ApiResultError,
  { id: number; body: KickoffQuestionUpdateBody }
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, { id: number; body: KickoffQuestionUpdateBody }>({
    mutationFn: updateKickoffQuestion,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KICKOFF_QUESTIONS_QUERY_KEY });
    },
  });
}

/** Delete (soft) mutation — invalidates the list. */
export function useDeleteKickoffQuestion(): UseMutationResult<void, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, number>({
    mutationFn: deleteKickoffQuestion,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KICKOFF_QUESTIONS_QUERY_KEY });
    },
  });
}

// ===========================================================================
// PT — position types
// ===========================================================================

/** Fetch all active position types (server orders sort_order, name). */
async function fetchPositionTypes(): Promise<PositionTypeDTO[]> {
  const result = await apiClient.get<unknown>('/admin/position-types', { command: PT_COMMAND });
  return positionTypesListResponseSchema.parse(unwrap(result)).types;
}

/** Create one position type (name required). */
async function createPositionType(body: PositionTypeSaveBody): Promise<void> {
  const result = await apiClient.post<unknown>('/admin/position-types', body, {
    command: PT_COMMAND,
  });
  positionTypeCreateResponseSchema.parse(unwrap(result));
}

/** Update one position type by id. */
async function updatePositionType(args: { id: number; body: PositionTypeSaveBody }): Promise<void> {
  const result = await apiClient.patch<unknown>(`/admin/position-types/${args.id}`, args.body, {
    command: PT_COMMAND,
  });
  adminMutationOkResponseSchema.parse(unwrap(result));
}

/** SOFT delete one position type by id. */
async function deletePositionType(id: number): Promise<void> {
  const result = await apiClient.delete<unknown>(`/admin/position-types/${id}`, {
    command: PT_COMMAND,
  });
  adminMutationOkResponseSchema.parse(unwrap(result));
}

/** List query — the Position Types tab. */
export function usePositionTypes(): UseQueryResult<PositionTypeDTO[], ApiResultError> {
  return useQuery<PositionTypeDTO[], ApiResultError>({
    queryKey: POSITION_TYPES_QUERY_KEY,
    queryFn: fetchPositionTypes,
  });
}

/** Create mutation — invalidates the list. */
export function useCreatePositionType(): UseMutationResult<
  void,
  ApiResultError,
  PositionTypeSaveBody
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, PositionTypeSaveBody>({
    mutationFn: createPositionType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POSITION_TYPES_QUERY_KEY });
    },
  });
}

/** Update mutation — invalidates the list. */
export function useUpdatePositionType(): UseMutationResult<
  void,
  ApiResultError,
  { id: number; body: PositionTypeSaveBody }
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, { id: number; body: PositionTypeSaveBody }>({
    mutationFn: updatePositionType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POSITION_TYPES_QUERY_KEY });
    },
  });
}

/** Delete (soft) mutation — invalidates the list. */
export function useDeletePositionType(): UseMutationResult<void, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, number>({
    mutationFn: deletePositionType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POSITION_TYPES_QUERY_KEY });
    },
  });
}
